import ResponseError from '../../types/response/ResponseError';
import { SignupFieldPath, signupFieldPaths } from '../../types/merchantSignup/MerchantSignup';

/**
 * The errors the merchant signup endpoints reject with, translated from the API's ResponseError so the page only has
 * to word them. Kept apart from merchantSignup.ts so tests that mock the requests can still throw them.
 */

export const REQUEST_INVALID_CODE = 'REQUEST_INVALID';
export const COMPANY_NOT_FOUND_CODE = 'MERCHANT_SIGNUP_COMPANY_NOT_FOUND';

/**
 * notFound: 404, invalidNumber: 422, registryUnavailable: 502, tooManyRequests: 429, failed: anything else. A 404 is
 * also what the API answers while signup is turned off.
 */
export type CompanyLookupFailure =
    | 'notFound'
    | 'invalidNumber'
    | 'registryUnavailable'
    | 'tooManyRequests'
    | 'failed';

export class CompanyLookupError extends Error {
    constructor(readonly reason: CompanyLookupFailure) {
        super(`The company lookup failed: ${reason}`);
        this.name = 'CompanyLookupError';
    }

    static from(error: unknown): CompanyLookupError {
        switch ((error as ResponseError)?.status) {
            case 404:
                return new CompanyLookupError('notFound');
            case 422:
                return new CompanyLookupError('invalidNumber');
            case 429:
                return new CompanyLookupError('tooManyRequests');
            case 502:
                return new CompanyLookupError('registryUnavailable');
            default:
                return new CompanyLookupError('failed');
        }
    }
}

/**
 * companyNotFound: the register no longer knows the company; companyRejected: another 422 on the company, e.g. the
 * organization number; rejected: 422 with field errors; tooManyRequests: 429; unavailable: 502 or 503, the register
 * or the signup queue is down; failed: anything else.
 */
export type SignupFailure =
    | 'companyNotFound'
    | 'companyRejected'
    | 'rejected'
    | 'tooManyRequests'
    | 'unavailable'
    | 'failed';

export interface SignupFieldError {
    path: SignupFieldPath;
    message: string;
}

/**
 * The request path a field error names, or undefined when it is not a field of the form. The API names fields by
 * their C# property path (HubUser.Email), or by their JSON path ($.hubUser.email) when the body could not be read.
 */
const toSignupFieldPath = (name: string): SignupFieldPath | undefined => {
    const normalized = name.replace(/^\$\./, '').toLowerCase();

    return signupFieldPaths.find(path => path.toLowerCase() === normalized);
};

export class SignupError extends Error {
    constructor(
        readonly reason: SignupFailure,
        /** The errors the API reported on a field of the request. */
        readonly fieldErrors: SignupFieldError[] = [],
        /** The messages of the errors not on a field of the request. */
        readonly otherErrors: string[] = []
    ) {
        super(`The signup failed: ${reason}`);
        this.name = 'SignupError';
    }

    static from(error: unknown): SignupError {
        const responseError = error as ResponseError;

        switch (responseError?.status) {
            case 422:
                break;
            case 429:
                return new SignupError('tooManyRequests');
            case 502:
            case 503:
                return new SignupError('unavailable');
            default:
                return new SignupError('failed');
        }

        if (responseError.code === COMPANY_NOT_FOUND_CODE)
            return new SignupError('companyNotFound');
        if (responseError.code !== REQUEST_INVALID_CODE) return new SignupError('companyRejected');
        if (!Array.isArray(responseError.errors)) return new SignupError('failed');

        const errors = responseError.errors.map(({ name, code, message }) => ({
            path: toSignupFieldPath(name),
            message: message || code,
        }));

        return new SignupError(
            'rejected',
            errors.flatMap(({ path, message }) => (path ? [{ path, message }] : [])),
            errors.filter(({ path }) => !path).map(({ message }) => message)
        );
    }
}
